import { isNil } from 'lodash/fp';
import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { decode } from 'js-base64';
import styled from 'styled-components';
import { gzip } from 'pako';
import {
    AbortError,
    AsyncMetaState,
    createMemoryMonitoringService,
    FeatureFlagServiceProps,
    getNewRelicMemoryEvent,
    MemoryMonitoringService,
    withFeatureFlagService,
} from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { FeatureFlag, RouteName } from '@mews-commander/enums';
import { DangerouslySetHtmlContent, ErrorHandler, OverlayedLoader } from '@mews-commander/ui';
import { PlatformEnvironment } from '@mews/enums';
import { createUrlFromPath, getPathFromUrl } from '@mews-commander/utils';
import { getCurrentEnterpriseId, getCurrentProfileId } from '@mews-commander/core';
import { getCurrentProfileScopeId } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import {
    META_STATE_FAILED,
    META_STATE_STARTED,
    META_STATE_SUCCEEDED,
} from '@mews-commander/legacy-entities/src/constants';
import {
    getPlatformEnvironment,
    getPlatformPrimaryUrl,
    getRouterBaseUrl,
    getServerBasePath,
} from '../../reducers/configuration';
import { changeDocumentTitle } from '../../../../middleware/createDocumentTitleMiddleware';
import { fetchTemplate, forceReload, navigateInModal, refreshPage } from '../../actions/common';
import { getModalScrollableElement } from '../../../modalManager/utils';
import { getRouterBasePathRegex } from '../../../navigation';
import { reportNewRelicEvents } from '../../actions/newRelic';
import { hasNavigatedBack } from '../../../../core/history';
import { BACKEND_TEMPLATES_CACHE, CACHE_ENABLED_BACKEND_PAGES } from '../../../../core/backendTemplatesCaching';
import { getLoaderVisible, pageLoaderStore } from '../../../../core/pageLoader';
import { ApiGeneratedAlertType, getErrorTexts, getStacktraceFromErrorTemplate, showAlert } from './utils';

interface OwnProps {
    url: string;
    layout?: string;
    returnHtmlOnly?: boolean;
    previousUrl?: string;
    className?: string;
    loadingOverlayFixed?: boolean;
    compressQueryString?: boolean;
    scrollRestoration?: { start: () => void; stop: () => void };
    routeName?: RouteName;
}

interface StateProps {
    showStackTrace: boolean;
    scopeId: string;
    enterpriseId: string;
    platformPrimaryUrl: string;
    serverBasePath: string;
    routerBasePath: string;
    routerBasePathRegex: RegExp;
}

interface DispatchProps {
    fetchTemplate: (...args: Parameters<typeof fetchTemplate>) => Promise<{
        data: unknown;
        headers: Headers;
        redirected: boolean;
    }>;
    navigate: (url: string) => void;
    reloadWindow: () => void;
    changeDocumentTitle: (title: string) => void;
    forceReload: () => void;
    reportNewRelicEvents: (...args: Parameters<typeof reportNewRelicEvents>) => void;
}

type TemplateFetcherClassProps = OwnProps &
    StateProps &
    DispatchProps &
    FeatureFlagServiceProps &
    Partial<typeof defaultProps>;

interface TemplateFetcherClassState {
    meta: AsyncMetaState;
    isLoaderVisible: boolean;
    htmlContent: string | Blob;
    errorMessage?: React.ReactNode;
    errorTitle?: React.ReactNode;
    stackTrace?: string | null;
    statusCode?: number;
}

const defaultProps = {
    layout: 'Modal',
    returnHtmlOnly: false,
};

export const compressToBase64 = (value: string) => {
    const compressed = gzip(`?${value}`);
    return Buffer.from(compressed).toString('base64');
};

const compressUrlQueryString = (url: URL) => {
    const newUrl = new URL(url.toString());
    newUrl.search = new URLSearchParams({
        compressed: 'true',
        base64data: compressToBase64(url.searchParams.toString()),
    }).toString();
    return newUrl;
};

export class TemplateFetcherClass extends Component<TemplateFetcherClassProps, TemplateFetcherClassState> {
    static defaultProps = defaultProps;

    handleTryAgain?: () => Promise<void>;
    scrollPosition?: number | null;

    state: TemplateFetcherClassState = {
        htmlContent: '',
        isLoaderVisible: getLoaderVisible(),
        ...META_STATE_SUCCEEDED,
    };

    fetchController = new window.AbortController();

    htmlContentRef = createRef<HTMLDivElement>();
    lastFetchMethod = '';
    counter = 0;
    keepScrollPosition = false;

    memoryMonitoringService: MemoryMonitoringService | null = null;
    rootsCount: object = {};
    unsubscribeFromPageLoader?: () => void;

    componentDidMount() {
        /**
         * The latest response is stored for a few selected pages to be used on navigation back instead of fetching again.
         * This can bring faster speed in trade of potentially stale data. It's a good enough trade off until the pages
         * are fully migrated on FE and leverage tanstack-query data cache instead.
         */
        if (
            hasNavigatedBack() &&
            this.props.routeName &&
            CACHE_ENABLED_BACKEND_PAGES.has(this.props.routeName) &&
            BACKEND_TEMPLATES_CACHE.has(this.props.url)
        ) {
            const htmlContent = BACKEND_TEMPLATES_CACHE.get(this.props.url) as string;
            this.setState({ htmlContent });
        } else {
            this.handleFetchHtmlContent();
        }

        this.memoryMonitoringService = createMemoryMonitoringService({
            context: window,
            report: (event) => {
                const newRelicEvent = getNewRelicMemoryEvent(event);
                this.props.reportNewRelicEvents([{ ...newRelicEvent, ...this.rootsCount, url: this.props.url }]);
            },
        });

        this.unsubscribeFromPageLoader = pageLoaderStore.subscribe((state) => {
            this.setState({ isLoaderVisible: state.loaderVisible });
        });
    }

    getSnapshotBeforeUpdate(prevProps: TemplateFetcherClassProps, prevState: TemplateFetcherClassState) {
        if (
            prevProps.url === this.props.url &&
            !prevState.meta.isFetching &&
            this.state.meta.isFetching &&
            this.keepScrollPosition
        ) {
            const scrollElement = getModalScrollableElement(this.htmlContentRef.current);
            this.scrollPosition = scrollElement ? scrollElement.scrollTop : null;
            this.keepScrollPosition = false;
        }

        return null;
    }

    componentDidUpdate({ url }: TemplateFetcherClassProps, prevState: TemplateFetcherClassState) {
        if (this.props.url !== url) {
            this.handleFetchHtmlContent();
            this.rootsCount = {};
        }

        if (
            prevState.meta.isFetching &&
            !this.state.meta.isFetching &&
            !this.state.meta.hasFailed &&
            this.scrollPosition
        ) {
            window.setTimeout(() => {
                const scrollElement = getModalScrollableElement(this.htmlContentRef.current);
                if (scrollElement) {
                    scrollElement.scrollTop = this.scrollPosition as number;
                }
                this.scrollPosition = null;
            });
        }
    }

    componentWillUnmount() {
        this.fetchController.abort();
        this.memoryMonitoringService?.stop();
        this.memoryMonitoringService = null;

        if (this.unsubscribeFromPageLoader) {
            this.unsubscribeFromPageLoader();
        }
    }

    reload = ({ keepScrollPosition }: { keepScrollPosition: boolean }) => {
        this.keepScrollPosition = keepScrollPosition;
        return this.handleFetchHtmlContent();
    };

    getPlatformUrl = (url: string) => {
        const { routerBasePathRegex, routerBasePath, serverBasePath, platformPrimaryUrl } = this.props;

        let normalizedUrl = url;
        /**
         * If the url matches an application format, we turn it into a server format
         */
        if (routerBasePathRegex.test(normalizedUrl)) {
            normalizedUrl = `${serverBasePath}${normalizedUrl.substring(routerBasePath.length)}`;
        }

        const finalUrl = `${platformPrimaryUrl}${normalizedUrl}`;
        return finalUrl;
    };

    getEncodedPlatformUrl = (url: string) => encodeURI(this.getPlatformUrl(url));

    handleFetchHtmlContent = () => {
        const options = {
            method: 'GET',
            accept: '*/*',
            customHeaders: {
                ...(this.props.previousUrl
                    ? { 'X-Mews-Referer': this.getEncodedPlatformUrl(this.props.previousUrl) }
                    : undefined),
            },
        };

        const profileId = getCurrentProfileId();
        const url = createUrlFromPath(this.props.url);
        const { searchParams } = url;
        searchParams.set('Layout', this.props.layout ?? defaultProps.layout);
        searchParams.set('ScopeId', this.props.scopeId);
        searchParams.set('ProfileId', profileId);
        searchParams.set('ReturnHtmlOnly', String(this.props.returnHtmlOnly));

        const enterpriseId = searchParams.get('EnterpriseId');
        if (!enterpriseId) {
            searchParams.set('EnterpriseId', this.props.enterpriseId);
        }

        const finalUrl = getPathFromUrl(
            this.props.featureFlagService.hasFlag(FeatureFlag.queryStringCompressionEnabled) &&
                this.props.compressQueryString
                ? compressUrlQueryString(url)
                : url
        );
        this.handleFetch({ url: finalUrl, options });
    };

    handlePostFormSubmit = ({ url, data, shouldHardReload }: $TSFixMe) => {
        const profileId = getCurrentProfileId();

        const options = {
            method: 'POST',
            accept: '*/*',
            autoContentType: true,
            customHeaders: {
                'X-Mews-Referer': this.getEncodedPlatformUrl(this.props.url),
            },
        };

        data.append('Layout', this.props.layout);
        data.append('ScopeId', this.props.scopeId);
        data.append('ProfileId', profileId);
        data.append('ReturnHtmlOnly', this.props.returnHtmlOnly);

        const enterpriseId = data.get('EnterpriseId');
        if (!enterpriseId) {
            data.append('EnterpriseId', this.props.enterpriseId);
        }

        this.handleFetch({ url, options, data, shouldHardReload });
    };

    handleFetch = ({ url, options = {}, data = undefined, shouldHardReload = false }: $TSFixMe) => {
        if (isNil(url)) {
            return;
        }

        this.lastFetchMethod = options.method;
        const finalOptions = {
            ...options,
            signal: this.fetchController.signal,
            credentials: 'include',
            shouldReturnResponseData: true,
        };

        const finalUrl = this.getPlatformUrl(url);

        const request = async () => {
            const currentRequestId = ++this.counter;
            this.setState({ ...META_STATE_STARTED });

            try {
                const {
                    data: responseData,
                    headers,
                    redirected,
                } = await this.props.fetchTemplate(finalUrl, finalOptions, data);

                if (currentRequestId === this.counter) {
                    if (redirected) {
                        const { serverBasePath, routerBasePath } = this.props;
                        const requestUrl = headers.get('RequestUrl') ?? '';
                        /**
                         * `requestUrl` is coming from server thus always prefixed with the default path
                         */
                        const normalizedRequestUrl = requestUrl.replace(serverBasePath, routerBasePath);

                        const parsedUrl = createUrlFromPath(normalizedRequestUrl);
                        const { searchParams } = parsedUrl;

                        const flashType = searchParams.get('FlashType');
                        const flashMessage = searchParams.get('FlashMessage');
                        if (flashType && flashMessage) {
                            showAlert({ message: flashMessage, type: flashType as ApiGeneratedAlertType });
                        }

                        searchParams.delete('Layout');
                        searchParams.delete('FlashType');
                        searchParams.delete('FlashMessage');

                        if (areUrlsEqual(parsedUrl, createUrlFromPath(this.props.url))) {
                            this.props.reloadWindow();
                        } else {
                            const cleanedUrl = getPathFromUrl(parsedUrl);
                            this.props.navigate(cleanedUrl);
                        }

                        if (shouldHardReload) {
                            this.props.forceReload();
                        }

                        return;
                    }

                    // Update title
                    const encodedTitle = headers.get('X-Mews-TitleBase64');
                    if (encodedTitle) {
                        const title = decode(encodedTitle);
                        this.props.changeDocumentTitle(title);
                    }

                    this.setState({ ...META_STATE_SUCCEEDED, htmlContent: responseData as string | Blob });

                    /**
                     * The latest response is stored for a few selected pages to be used on navigation back instead of fetching again.
                     * This can bring faster speed in trade of potentially stale data. It's a good enough trade off until the pages
                     * are fully migrated on FE and leverage tanstack-query data cache instead.
                     */
                    if (this.props.routeName && CACHE_ENABLED_BACKEND_PAGES.has(this.props.routeName)) {
                        BACKEND_TEMPLATES_CACHE.set(this.props.url, responseData as string);
                    }

                    if (shouldHardReload) {
                        this.props.forceReload();
                    }
                }
            } catch (error) {
                if (currentRequestId === this.counter && !(error instanceof AbortError)) {
                    const { data: errorData, statusCode } = error;
                    const { errorMessage, errorTitle } = getErrorTexts(statusCode);
                    this.setState({ ...META_STATE_FAILED, errorTitle, errorMessage, statusCode });

                    if (this.props.showStackTrace) {
                        const stackTrace =
                            typeof errorData === 'string' ? getStacktraceFromErrorTemplate(errorData) : null;
                        this.setState({ stackTrace });
                    }
                }
            }
        };

        this.handleTryAgain = request;
        void request();
    };

    handleErrorDismiss = () => {
        this.setState({ ...META_STATE_SUCCEEDED });
    };

    handleContentRender = (rootsCount: object) => {
        this.rootsCount = rootsCount;
    };

    handleContentUnmount = () => {
        this.rootsCount = {};
    };

    render() {
        const { className, scrollRestoration } = this.props;

        const { meta, htmlContent, errorMessage, errorTitle, stackTrace, statusCode, isLoaderVisible } = this.state;
        const { isFetching, hasFailed } = meta;

        if (hasFailed) {
            return (
                <div className={className}>
                    <ModalScrollContainer>
                        <ErrorHandler
                            errorTitle={errorTitle}
                            errorMessage={errorMessage}
                            stackTrace={stackTrace}
                            onTryAgain={statusCode && statusCode >= 500 ? this.handleTryAgain : undefined}
                            onDismiss={this.lastFetchMethod === 'POST' ? this.handleErrorDismiss : undefined}
                        />
                    </ModalScrollContainer>
                </div>
            );
        }

        return (
            <>
                {isFetching || isLoaderVisible ? <OverlayedLoader fixed={this.props.loadingOverlayFixed} show /> : null}
                <DangerouslySetHtmlContent
                    className={className}
                    ref={this.htmlContentRef}
                    html={htmlContent}
                    scrollRestoration={scrollRestoration}
                    onRender={this.handleContentRender}
                    onUnmount={this.handleContentUnmount}
                />
            </>
        );
    }
}

const ModalScrollContainer = styled.div`
    flex: 1 1 auto !important;
    overflow: hidden auto;
    position: relative;
`;

function areUrlsEqual(urlA: URL, urlB: URL) {
    return urlA.pathname === urlB.pathname && normalizedSearch(urlA.search) === normalizedSearch(urlB.search);
}

function normalizedSearch(search: string) {
    const searchParams = new URLSearchParams(search);
    const sortedParams = [...searchParams.entries()].sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
    return new URLSearchParams(sortedParams).toString();
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state) => ({
        showStackTrace:
            getPlatformEnvironment() !== PlatformEnvironment.Production &&
            getPlatformEnvironment() !== PlatformEnvironment.Demo,
        scopeId: getCurrentProfileScopeId(state),
        enterpriseId: getCurrentEnterpriseId(),
        platformPrimaryUrl: getPlatformPrimaryUrl(),
        serverBasePath: getServerBasePath(),
        routerBasePath: getRouterBaseUrl(),
        routerBasePathRegex: getRouterBasePathRegex(state),
    }),
    {
        fetchTemplate,
        navigate: navigateInModal,
        reloadWindow: refreshPage,
        changeDocumentTitle,
        forceReload,
        reportNewRelicEvents,
    } as unknown as DispatchProps,
    undefined,
    {
        forwardRef: true,
    }
)(withFeatureFlagService(TemplateFetcherClass));
